"use client"

/**
 * Prostate MRI bespoke "what's included" section.
 *
 * Single-purpose page (/tests/prostate-mri) — no men/women toggle. Two-column
 * layout: left rail (heading + checkmark takeaways + CTA), right grey card with
 * a white risk-score lede above the quick-facts table, mirroring the
 * full-body-MRI design.
 *
 * Copy lives in the `tests` namespace (messages/*.json) with mirror-locale
 * parity; only the icon + layout structure is in code.
 */

import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { BookingCta } from "@/components/ui/booking-cta"
import { buttonVariants } from "@/components/ui/button"
import type { ProductCategory } from "@/i18n/config"
import {
  GreenCheck,
  InfoTimeIcon,
  InfoRadiationIcon,
  InfoHealthAreasIcon,
  InfoPreparationIcon,
  InfoForWhomIcon,
} from "@/components/ui/icons"
import type { ReactNode } from "react"
import { FactRows } from "@/components/ui/quick-facts-section"

const iconClass = "size-5 shrink-0 text-neutral-900"

/** Quick-facts rows — icon + layout in code, text from the `tests` namespace
 *  via `<keyBase>Label` / `<keyBase>Value` / `<keyBase>Supporting`. */
const QUICK_FACTS: { icon: ReactNode; keyBase: string }[] = [
  { icon: <InfoTimeIcon className={iconClass} />, keyBase: "prostateMriFact1" },
  {
    icon: <InfoRadiationIcon className={iconClass} />,
    keyBase: "prostateMriFact2",
  },
  {
    icon: <InfoHealthAreasIcon className={iconClass} />,
    keyBase: "prostateMriFact3",
  },
  {
    icon: <InfoPreparationIcon className={iconClass} />,
    keyBase: "prostateMriFact4",
  },
  {
    icon: <InfoForWhomIcon className={iconClass} />,
    keyBase: "prostateMriFact5",
  },
]

const CHECKPOINT_KEYS = [
  "prostateMriCheckpoint1",
  "prostateMriCheckpoint2",
  "prostateMriCheckpoint3",
  "prostateMriCheckpoint4",
  "prostateMriCheckpoint5",
]

/** Condensed green-check takeaways. */
function CheckpointList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-4">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <GreenCheck width={20} height={20} className="mt-0.5 shrink-0" />
          <span className="type-body-lg text-pretty text-neutral-700">
            {item}
          </span>
        </li>
      ))}
    </ul>
  )
}

/** The membership-first table's icons, in `QUICK_FACTS` order. */
const ROW_ICON_KEYS = [
  "clock",
  "no-radiation",
  "droplet",
  "checklist",
  "person",
]

export interface ProstateMriSectionProps {
  /** The membership-first site: the plain fact table, and the hero's
   *  button as the only booking one (Eugene, 02.10). */
  simple?: boolean
  ctaLabel: string
  ctaCategory: ProductCategory
  bookingProductId?: string
  trackingLocation: string
}

export function ProstateMriSection({
  simple = false,
  ctaLabel,
  ctaCategory,
  bookingProductId,
  trackingLocation,
}: ProstateMriSectionProps) {
  const tt = useTranslations("tests") as unknown as (k: string) => string
  const ut = useTranslations("ui")

  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Two-column content */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:items-stretch lg:gap-20">
          {/* Left rail */}
          <div className="flex flex-col justify-between">
            <div>
              <h2 className="type-display-sm text-neutral-900">
                {tt("prostateMriSectionHeading")}
              </h2>
              <div className="mt-8">
                <CheckpointList items={CHECKPOINT_KEYS.map((k) => tt(k))} />
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              {!simple && (
                <BookingCta
                  category={ctaCategory}
                  productId={bookingProductId}
                  trackingLabel={ctaLabel}
                  trackingLocation={`${trackingLocation}-prostate`}
                  className={cn(
                    buttonVariants({ variant: "primary" }),
                    "w-full sm:w-auto"
                  )}
                >
                  {ctaLabel}
                </BookingCta>
              )}
              <a
                href="#biomarkers"
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "w-full sm:w-auto"
                )}
              >
                {simple ? ut("seeEverythingWeCheck") : ut("viewBiomarkersList")}
              </a>
            </div>
          </div>

          {/* Right column — grey quick-facts card. */}
          {simple ? (
            <FactRows
              rows={QUICK_FACTS.map((qf, i) => ({
                icon: ROW_ICON_KEYS[i],
                label: tt(`${qf.keyBase}Label`),
                main: tt(`${qf.keyBase}Value`),
                sub: tt(`${qf.keyBase}Supporting`),
              }))}
            />
          ) : (
            <div className="rounded-[16px] bg-neutral-100 px-6 py-6 md:px-10 md:py-8">
              <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-[auto_1fr] sm:items-start sm:gap-x-6 sm:gap-y-6">
                {QUICK_FACTS.map((qf) => (
                  <li
                    key={qf.keyBase}
                    className="grid grid-cols-1 gap-1 py-3 sm:contents"
                  >
                    <div className="flex items-center gap-4">
                      {qf.icon}
                      <span className="type-body-lg text-neutral-900">
                        {tt(`${qf.keyBase}Label`)}
                      </span>
                    </div>
                    <span className="type-body-lg text-left text-neutral-600">
                      <span className="block">{tt(`${qf.keyBase}Value`)}</span>
                      {tt(`${qf.keyBase}Supporting`)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
